import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Swap, Plus, X, CheckCircle, Archive, Trash, ChatCircleDots, PaperPlaneTilt, GearSix, Notebook, ArrowsLeftRight, MagnifyingGlass } from "@phosphor-icons/react";
import { DeptChips, useDepartments } from "../components/DepartmentPicker";

const KIND_META = {
  ops: { label: "Ops Handover", chip: "bg-slate-100 text-slate-600" },
  gm_briefing: { label: "GM Briefing", chip: "bg-violet-50 text-violet-700 border border-violet-200" },
  cross: { label: "Cross-Department", chip: "bg-indigo-50 text-indigo-700 border border-indigo-200" },
  directive: { label: "GM Directive", chip: "bg-amber-50 text-amber-800 border border-amber-200" },
};

export default function HandoverPage() {
  const { user } = useAuth();
  const departments = useDepartments();
  const [tab, setTab] = useState("today");
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState({ categories: [], tags: [] });
  const [team, setTeam] = useState([]);
  const [deptFilter, setDeptFilter] = useState("");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [cfgDraft, setCfgDraft] = useState(null);
  const [newCat, setNewCat] = useState("");
  const [newTag, setNewTag] = useState("");
  const [drafts, setDrafts] = useState({});

  const myDepts = user.department_ids || [];
  const isTopAdmin = ["admin", "gm"].includes(user.role) || user.is_master_operator;
  const isManagerUp = ["admin", "gm", "manager"].includes(user.role);
  const scoped = myDepts.length > 0 && !isTopAdmin;
  const canDispatchTasks = user.role !== "staff" || user.can_allocate_handover_tasks === true;
  const activeDepts = departments.filter((d) => d.active !== false);
  const deptName = (id) => departments.find((d) => d.id === id)?.name || "—";

  const load = () => {
    const params = { scope: tab === "today" ? "today" : "all" };
    if (tab === "archived") params.archived = "true";
    if (tab === "vault" && q.trim()) params.q = q.trim();
    if (deptFilter && isTopAdmin) params.department_id = deptFilter;
    api.get("/handover", { params }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, [tab, deptFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/handover-config").then((r) => setConfig(r.data)).catch(() => {});
    if (isManagerUp) api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const kindOptions = isTopAdmin
    ? [["ops", "Ops Handover"], ["cross", "Cross-Department"], ["directive", "GM Directive"]]
    : user.role === "manager"
      ? [["ops", "Ops Handover"], ["gm_briefing", "GM Briefing"], ["cross", "Cross-Department"]]
      : [["ops", "Ops Handover"]];

  const openForm = (kind = "ops", targetDept = "") => {
    setForm({
      kind, category: config.categories[0] || "", tags: [], summary: "",
      shift_label: "PM", cash_status: "", low_stock: "", equipment_issues: "",
      pending: "", require_photo_proof: true,
      department_ids: scoped ? [...myDepts] : [],
      target_department_id: targetDept, assigned_user_ids: [],
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.summary.trim()) return toast.error("A summary is required");
    if (["cross", "directive"].includes(form.kind) && !form.target_department_id) return toast.error("Select the target department");
    setBusy(true);
    try {
      await api.post("/handover", {
        kind: form.kind, category: form.category, tags: form.tags,
        summary: form.summary, shift_label: form.shift_label,
        cash_status: form.kind === "ops" ? form.cash_status : "",
        low_stock: form.kind === "ops" ? form.low_stock.split(",").map((s) => s.trim()).filter(Boolean) : [],
        equipment_issues: form.kind === "ops" ? form.equipment_issues : "",
        pending_tasks: form.kind === "ops" && canDispatchTasks ? form.pending.split("\n").map((s) => s.trim()).filter(Boolean) : [],
        require_photo_proof: form.require_photo_proof,
        department_ids: form.department_ids,
        target_department_ids: form.target_department_id ? [form.target_department_id] : [],
        assigned_user_ids: form.assigned_user_ids,
      });
      toast.success(form.kind === "directive" ? "Directive dispatched to the department manager"
        : form.kind === "gm_briefing" ? "Briefing sent up to the GM"
          : form.kind === "cross" ? "Cross-department handover sent" : "Handover logged");
      setShowForm(false); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const ack = async (id) => {
    try { await api.post(`/handover/${id}/ack`); toast.success("Acknowledged"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const archiveH = async (id, val = true) => {
    try { await api.post(`/handover/${id}/archive`, { archived: val }); toast.success(val ? "Archived" : "Restored"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeH = async (id) => {
    if (!window.confirm("Permanently delete this handover?")) return;
    try { await api.delete(`/handover/${id}`); toast.success("Handover deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const sendComment = async (id) => {
    const text = (drafts[id] || "").trim();
    if (!text) return;
    try {
      await api.post(`/handover/${id}/comment`, { text });
      setDrafts({ ...drafts, [id]: "" });
      toast.success("Comment added"); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openSettings = () => {
    setCfgDraft({ categories: [...config.categories], tags: [...config.tags] });
    setShowSettings(true);
  };
  const saveConfig = async () => {
    try {
      const { data } = await api.put("/handover-config", cfgDraft);
      setConfig(data); setShowSettings(false);
      toast.success("Handover settings saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleDelegate = async (m) => {
    try {
      await api.put(`/team/${m.id}`, { can_allocate_handover_tasks: !m.can_allocate_handover_tasks });
      setTeam(team.map((t) => t.id === m.id ? { ...t, can_allocate_handover_tasks: !m.can_allocate_handover_tasks } : t));
      toast.success(!m.can_allocate_handover_tasks ? `${m.name} can now allocate tasks from handovers` : `Task allocation revoked for ${m.name}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const isIncoming = (h) => isTopAdmin || (h.target_department_ids || []).some((d) => myDepts.includes(d));
  const ops = items.filter((h) => !h.kind || h.kind === "ops");
  const cross = items.filter((h) => h.kind === "cross");
  const directives = items.filter((h) => h.kind === "directive");
  const briefings = items.filter((h) => h.kind === "gm_briefing");

  const Card = ({ h }) => {
    const meta = KIND_META[h.kind] || KIND_META.ops;
    return (
      <div data-testid={`handover-card-${h.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${meta.chip}`}>{meta.label}</span>
            {h.category && <span className="text-[10px] font-bold uppercase tracking-wider bg-sky-50 text-sky-700 border border-sky-200 px-2.5 py-1 rounded-full">{h.category}</span>}
            {(h.tags || []).map((t) => (
              <span key={t} className="text-[10px] font-bold uppercase tracking-wider bg-slate-50 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full">#{t}</span>
            ))}
            {h.kind === "cross" && (h.target_department_ids || []).length > 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-2.5 py-1 rounded-full flex items-center gap-1">
                <ArrowsLeftRight size={11} /> {deptName((h.origin_department_ids || [])[0]) !== "—" ? `${deptName((h.origin_department_ids || [])[0])} → ` : ""}{(h.target_department_ids || []).map(deptName).join(", ")}
              </span>
            )}
            {h.kind === "directive" && (h.target_department_ids || []).length > 0 && (
              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-600 text-white px-2.5 py-1 rounded-full">To: {(h.target_department_ids || []).map(deptName).join(", ")}</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isManagerUp && !h.acknowledged && (
              <button data-testid={`handover-ack-${h.id}`} onClick={() => ack(h.id)}
                className="h-9 px-4 rounded-full border border-emerald-200 text-xs font-bold text-emerald-700 flex items-center gap-1.5 tap-press hover:bg-emerald-50">
                <CheckCircle size={14} /> Acknowledge
              </button>
            )}
            {h.acknowledged && <span className="text-xs font-bold text-emerald-600 flex items-center gap-1"><CheckCircle size={14} /> {h.acknowledged_by || "Acknowledged"}</span>}
            {isManagerUp && (
              <>
                {tab === "archived" ? (
                  <button data-testid={`handover-restore-${h.id}`} onClick={() => archiveH(h.id, false)}
                    className="h-9 px-3 rounded-full border border-slate-200 text-xs font-bold text-slate-500 tap-press hover:border-slate-900">Restore</button>
                ) : (
                  <button data-testid={`handover-archive-${h.id}`} onClick={() => archiveH(h.id)} title="Archive"
                    className="h-9 w-9 rounded-full border border-slate-200 text-slate-400 flex items-center justify-center tap-press hover:text-slate-900 hover:border-slate-900"><Archive size={15} /></button>
                )}
                <button data-testid={`handover-delete-${h.id}`} onClick={() => removeH(h.id)} title="Delete"
                  className="h-9 w-9 rounded-full border border-slate-200 text-slate-400 flex items-center justify-center tap-press hover:text-red-500 hover:border-red-300"><Trash size={15} /></button>
              </>
            )}
          </div>
        </div>
        <p className="text-sm text-slate-700 mt-3 whitespace-pre-wrap">{h.summary}</p>
        <div className="flex flex-wrap gap-x-5 gap-y-1 mt-3 text-xs text-slate-500">
          {h.cash_status && <span><b>Cash:</b> {h.cash_status}</span>}
          {(h.low_stock || []).length > 0 && <span><b>Low stock:</b> {h.low_stock.join(", ")}</span>}
          {h.equipment_issues && <span className="text-red-600"><b>Equipment:</b> {h.equipment_issues}</span>}
          {h.dispatched_count > 0 && <span className="text-emerald-700"><b>{h.dispatched_count}</b> task{h.dispatched_count > 1 ? "s" : ""} dispatched</span>}
        </div>
        {(h.department_ids || []).length > 0 && <div className="mt-2"><DeptChips ids={h.department_ids} departments={departments} /></div>}
        <p className="text-xs text-slate-400 mt-3">
          {h.created_by_name} · {h.created_by_role} · {h.date} {h.shift_label ? `· ${h.shift_label}` : ""}
        </p>
        {(h.comments || []).length > 0 && (
          <div className="mt-3 space-y-2 border-t border-slate-100 pt-3" data-testid={`comments-${h.id}`}>
            {h.comments.map((c) => (
              <div key={c.id} className="bg-slate-50 rounded-xl px-3 py-2">
                <p className="text-xs text-slate-700">{c.text}</p>
                <p className="text-[10px] text-slate-400 mt-0.5 font-semibold">{c.by} · manager note</p>
              </div>
            ))}
          </div>
        )}
        {isManagerUp && tab !== "archived" && (
          <div className="flex gap-2 mt-3">
            <input data-testid={`comment-input-${h.id}`} value={drafts[h.id] || ""}
              onChange={(e) => setDrafts({ ...drafts, [h.id]: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && sendComment(h.id)}
              placeholder="Add a manager comment…" className="flex-1 h-10 px-4 rounded-full border border-slate-200 text-sm" />
            <button data-testid={`comment-send-${h.id}`} onClick={() => sendComment(h.id)}
              className="h-10 w-10 rounded-full bg-slate-900 text-white flex items-center justify-center tap-press hover:bg-slate-800">
              <PaperPlaneTilt size={16} />
            </button>
          </div>
        )}
      </div>
    );
  };

  const Section = ({ title, icon: Icon, list, testId, empty }) => (
    <section data-testid={testId}>
      <h2 className="font-heading text-lg font-bold text-slate-700 mb-3 flex items-center gap-2"><Icon size={20} weight="duotone" /> {title} · {list.length}</h2>
      <div className="space-y-3">
        {list.map((h) => <Card key={h.id} h={h} />)}
        {list.length === 0 && <p className="text-slate-400 text-sm">{empty}</p>}
      </div>
    </section>
  );

  return (
    <div className="space-y-8 rise-in" data-testid="handover-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] font-bold text-slate-400">Department Continuity</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Handovers</h1>
          <p className="text-slate-500 mt-2 text-sm">
            {isTopAdmin ? "Audit every department's handover log, comment, and dispatch directives."
              : user.role === "manager" ? "Your department's log, cross-department ledger and GM briefings."
                : "Log operational handovers for your department."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isManagerUp && (
            <button data-testid="handover-settings-button" onClick={openSettings}
              className="h-11 px-4 rounded-full border-2 border-slate-200 text-sm font-bold flex items-center gap-2 tap-press hover:border-slate-900">
              <GearSix size={18} /> Settings
            </button>
          )}
          {isTopAdmin && (
            <button data-testid="gm-directive-button" onClick={() => openForm("directive", deptFilter)}
              className="h-11 px-5 rounded-full bg-amber-500 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-amber-600">
              <Notebook size={18} /> GM Directive
            </button>
          )}
          <button data-testid="new-handover-button" onClick={() => openForm("ops")}
            className="h-11 px-5 rounded-full bg-slate-900 text-white text-sm font-bold flex items-center gap-2 tap-press hover:bg-slate-800">
            <Plus size={18} /> New handover
          </button>
        </div>
      </div>

      {isTopAdmin && (
        <div className="flex flex-wrap gap-2" data-testid="handover-dept-switcher">
          <button data-testid="handover-dept-all" onClick={() => setDeptFilter("")}
            className={`h-10 px-4 rounded-full text-xs font-bold tap-press border ${!deptFilter ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
            All departments
          </button>
          {activeDepts.map((d) => (
            <button key={d.id} data-testid={`handover-dept-switch-${d.id}`} onClick={() => setDeptFilter(deptFilter === d.id ? "" : d.id)}
              className={`h-10 px-4 rounded-full text-xs font-bold tap-press border ${deptFilter === d.id ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
              {d.name}
            </button>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        {[["today", "Today"], ["vault", "Vault"], ...(isManagerUp ? [["archived", "Archived"]] : [])].map(([k, l]) => (
          <button key={k} data-testid={`handover-tab-${k}`} onClick={() => setTab(k)}
            className={`h-10 px-5 rounded-full text-sm font-bold tap-press ${tab === k ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-500 hover:border-slate-900"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "vault" && (
        <div className="flex gap-2">
          <div className="relative flex-1">
            <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="vault-search-input" value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search summaries, categories, tags, people…" className="w-full h-11 pl-11 pr-4 rounded-xl border border-slate-200 text-sm" />
          </div>
          <button data-testid="vault-search-button" onClick={load}
            className="h-11 px-5 rounded-full bg-slate-900 text-white text-sm font-bold tap-press hover:bg-slate-800">Search</button>
        </div>
      )}

      {tab === "archived" ? (
        <Section title="Archived handovers" icon={Archive} list={items} testId="archived-section" empty="Archive is empty." />
      ) : (
        <>
          <Section title={user.role === "staff" ? "Department handover log" : "Department log"} icon={Swap} list={ops}
            testId="ops-section" empty={tab === "vault" ? "No matches found." : "No handovers logged yet today."} />
          {isManagerUp && (
            <Section title="Cross-Department Handovers" icon={ArrowsLeftRight}
              list={cross.filter((h) => tab === "vault" || isTopAdmin ? true : isIncoming(h) || h.created_by === user.id)}
              testId="cross-ledger" empty="No cross-department handovers — entries appear when another department tags yours." />
          )}
          {isManagerUp && (directives.length > 0 || isTopAdmin) && (
            <Section title="GM Directives" icon={Notebook} list={directives}
              testId="directives-section" empty="No directives issued." />
          )}
          {(isTopAdmin || briefings.length > 0) && isManagerUp && (
            <Section title="GM Briefings" icon={ChatCircleDots} list={briefings}
              testId="briefings-section" empty="No briefings sent up yet." />
          )}
        </>
      )}

      {/* ---------- FORM MODAL ---------- */}
      {showForm && form && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowForm(false)}>
          <div data-testid="handover-form-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 rise-in space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">New handover</h2>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            {kindOptions.length > 1 && (
              <div className="flex flex-wrap gap-2">
                {kindOptions.map(([k, l]) => (
                  <button key={k} type="button" data-testid={`kind-chip-${k}`} onClick={() => setForm({ ...form, kind: k })}
                    className={`h-9 px-4 rounded-full text-xs font-bold tap-press border ${form.kind === k ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                    {l}
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="handover-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                {config.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <select data-testid="handover-shift-select" value={form.shift_label} onChange={(e) => setForm({ ...form, shift_label: e.target.value })}
                className="h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                {["AM", "PM", "Night"].map((s) => <option key={s} value={s}>{s} shift</option>)}
              </select>
            </div>
            {config.tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {config.tags.map((t) => (
                  <button key={t} type="button" data-testid={`handover-tag-${t}`}
                    onClick={() => setForm({ ...form, tags: form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t] })}
                    className={`h-8 px-3 rounded-full text-xs font-bold tap-press border ${form.tags.includes(t) ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-400 border-slate-200 hover:border-slate-900"}`}>
                    #{t}
                  </button>
                ))}
              </div>
            )}
            {["cross", "directive"].includes(form.kind) && (
              <select data-testid="target-dept-select" value={form.target_department_id}
                onChange={(e) => setForm({ ...form, target_department_id: e.target.value })}
                className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white font-semibold text-sm">
                <option value="">Target department (required)…</option>
                {activeDepts.filter((d) => form.kind === "directive" || !scoped || !myDepts.includes(d.id))
                  .map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            )}
            <textarea data-testid="handover-summary-input" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })}
              placeholder={form.kind === "directive" ? "Operational instruction for the department manager…"
                : form.kind === "gm_briefing" ? "Briefing for the GM / Master Admin…"
                  : form.kind === "cross" ? "Note or request for the target department…"
                    : "What does the next shift need to know?"}
              rows={3} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
            {form.kind === "ops" && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="handover-cash-input" value={form.cash_status} onChange={(e) => setForm({ ...form, cash_status: e.target.value })}
                    placeholder="Cash / banking status" className="h-12 px-4 rounded-xl border border-slate-200 text-sm" />
                  <input data-testid="handover-stock-input" value={form.low_stock} onChange={(e) => setForm({ ...form, low_stock: e.target.value })}
                    placeholder="Low stock (comma-separated)" className="h-12 px-4 rounded-xl border border-slate-200 text-sm" />
                </div>
                <input data-testid="handover-equipment-input" value={form.equipment_issues} onChange={(e) => setForm({ ...form, equipment_issues: e.target.value })}
                  placeholder="Equipment issues (triggers red-flag alert)" className="w-full h-12 px-4 rounded-xl border border-slate-200 text-sm" />
                {canDispatchTasks ? (
                  <div className="bg-slate-50 border border-slate-100 rounded-xl p-4 space-y-3">
                    <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400">Dispatch follow-up tasks (one per line)</p>
                    <textarea data-testid="handover-tasks-input" value={form.pending} onChange={(e) => setForm({ ...form, pending: e.target.value })}
                      rows={2} placeholder={"Restock napkins\nCheck freezer temp"} className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm" />
                    <div className="flex flex-wrap gap-2">
                      {team.filter((m) => ["staff", "supervisor"].includes(m.role) &&
                        (!scoped || (m.department_ids || []).some((d) => myDepts.includes(d)))).map((m) => (
                        <button key={m.id} type="button" data-testid={`handover-assignee-${m.id}`}
                          onClick={() => setForm({ ...form, assigned_user_ids: form.assigned_user_ids.includes(m.id) ? form.assigned_user_ids.filter((a) => a !== m.id) : [...form.assigned_user_ids, m.id] })}
                          className={`h-8 px-3 rounded-full text-xs font-bold tap-press border ${form.assigned_user_ids.includes(m.id) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-500 border-slate-200 hover:border-slate-900"}`}>
                          {m.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <p data-testid="handover-tasks-disabled-note" className="text-[11px] font-semibold text-slate-500 bg-slate-50 border border-slate-100 rounded-lg px-3 py-2">
                    Task allocation is managed via Floor Tasks. Your manager can delegate handover task access from Settings.
                  </p>
                )}
              </>
            )}
            <button data-testid="submit-handover-button" onClick={submit} disabled={busy}
              className="w-full py-3.5 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800 disabled:opacity-50">
              {busy ? "Sending…" : form.kind === "directive" ? "Dispatch directive" : form.kind === "gm_briefing" ? "Send to GM" : form.kind === "cross" ? "Send to department" : "Log handover"}
            </button>
          </div>
        </div>
      )}

      {/* ---------- SETTINGS MODAL ---------- */}
      {showSettings && cfgDraft && (
        <div className="fixed inset-0 z-[1000] bg-slate-950/50 flex items-center justify-center p-4 sm:p-6" onClick={() => setShowSettings(false)}>
          <div data-testid="handover-settings-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white w-full sm:max-w-lg max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-6 sm:p-8 rise-in space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-bold">Handover settings</h2>
              <button onClick={() => setShowSettings(false)} className="p-2 rounded-full hover:bg-slate-100 tap-press"><X size={22} /></button>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Categories</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {cfgDraft.categories.map((c) => (
                  <span key={c} data-testid={`cfg-category-${c}`} className="h-9 pl-4 pr-2 rounded-full bg-sky-50 text-sky-700 border border-sky-200 text-xs font-bold flex items-center gap-1">
                    {c}
                    <button onClick={() => setCfgDraft({ ...cfgDraft, categories: cfgDraft.categories.filter((x) => x !== c) })}
                      className="p-1 hover:text-red-500 tap-press"><X size={12} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="cfg-add-category-input" value={newCat} onChange={(e) => setNewCat(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && newCat.trim()) { setCfgDraft({ ...cfgDraft, categories: [...cfgDraft.categories, newCat.trim()] }); setNewCat(""); } }}
                  placeholder="Add category…" className="flex-1 h-10 px-4 rounded-full border border-slate-200 text-sm" />
                <button data-testid="cfg-add-category-button"
                  onClick={() => { if (newCat.trim()) { setCfgDraft({ ...cfgDraft, categories: [...cfgDraft.categories, newCat.trim()] }); setNewCat(""); } }}
                  className="h-10 px-4 rounded-full bg-slate-900 text-white text-xs font-bold tap-press">Add</button>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-2">Tags</p>
              <div className="flex flex-wrap gap-2 mb-2">
                {cfgDraft.tags.map((t) => (
                  <span key={t} data-testid={`cfg-tag-${t}`} className="h-9 pl-4 pr-2 rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex items-center gap-1">
                    #{t}
                    <button onClick={() => setCfgDraft({ ...cfgDraft, tags: cfgDraft.tags.filter((x) => x !== t) })}
                      className="p-1 hover:text-red-500 tap-press"><X size={12} /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="cfg-add-tag-input" value={newTag} onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && newTag.trim()) { setCfgDraft({ ...cfgDraft, tags: [...cfgDraft.tags, newTag.trim().replace(/^#/, "")] }); setNewTag(""); } }}
                  placeholder="Add tag…" className="flex-1 h-10 px-4 rounded-full border border-slate-200 text-sm" />
                <button data-testid="cfg-add-tag-button"
                  onClick={() => { if (newTag.trim()) { setCfgDraft({ ...cfgDraft, tags: [...cfgDraft.tags, newTag.trim().replace(/^#/, "")] }); setNewTag(""); } }}
                  className="h-10 px-4 rounded-full bg-slate-900 text-white text-xs font-bold tap-press">Add</button>
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.15em] font-bold text-slate-400 mb-1">Delegate task allocation</p>
              <p className="text-[11px] text-slate-500 mb-2">Allow specific staff to dispatch follow-up tasks from their handovers.</p>
              <div className="space-y-2 max-h-44 overflow-y-auto">
                {team.filter((m) => m.role === "staff" && (!scoped || (m.department_ids || []).some((d) => myDepts.includes(d)))).map((m) => (
                  <div key={m.id} className="flex items-center justify-between gap-3 bg-slate-50 border border-slate-100 rounded-xl px-4 py-2.5">
                    <div>
                      <p className="text-sm font-bold">{m.name}</p>
                      <p className="text-[10px] text-slate-400">{(m.department_ids || []).map((id) => departments.find((d) => d.id === id)?.name).filter(Boolean).join(", ") || "No department"}</p>
                    </div>
                    <button data-testid={`delegate-toggle-${m.id}`} onClick={() => toggleDelegate(m)}
                      className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${m.can_allocate_handover_tasks ? "bg-emerald-500" : "bg-slate-300"}`}>
                      <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${m.can_allocate_handover_tasks ? "left-6" : "left-1"}`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <button data-testid="save-handover-config" onClick={saveConfig}
              className="w-full py-3.5 rounded-full bg-slate-900 text-white font-heading font-bold tap-press hover:bg-slate-800">
              Save settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
